import { computed, ref } from 'vue'
import { useStorage } from '@vueuse/core'
import { useTabStorage } from '@/composables/useTabSpecificStorage'
import { IDashboard, IDashboardCreate, IExtendedDashboard } from '@/types/dashboard'
import DashboardCachedDataService from '@/core/services/cachedData/DashboardCachedDataService'
import { DashboardApi } from '@/api/dashboardApi'
import GlobalCachedDataService from '@/core/services/cachedData/GlobalCachedDataService'
import { selectedAccountID } from '@/composables/globalData/useEntitiesData'
import { WidgetApi } from '@/api/widgetApi'
import Logger from '@/core/services/Logger'

const logger = Logger.makeSectionLogger('USE_DASHBOARD_DATA')

/* Constants */
const DASHBOARD_STORE_KEY = 'UserDashboards'
const MAX_EXTENDED_DASHBOARDS = 10  // Maximum number of extended dashboards to keep in cache

/* Types */
type ActiveStoreDashboardType = {
    activeDashboardID: number | undefined
    activeWidgetGroupID: number | undefined
}

const activeDashboardState = useTabStorage<ActiveStoreDashboardType>(DASHBOARD_STORE_KEY, {
    activeDashboardID: undefined,
    activeWidgetGroupID: undefined
})

/* Shared Data */
export const editModeState = ref(false)
export const isContentLoaded = ref(false)

/* Computed */
export const ActiveAccountDashboards = computed(() => {
    const dashboards = DashboardCachedDataService.dataRef.value
        .filter(dashboard => dashboard.AccountID === selectedAccountID.value)
    
    // Sort by order field in DashboardConf, fallback to DashboardID
    // This ensures dashboards appear in custom order in dropdown
    // and the first ordered dashboard becomes the default
    // Create a copy before sorting to avoid mutating the original cached array
    return [ ...dashboards ].sort((a, b) => {
        const orderA = a.DashboardConf?.order ?? Infinity
        const orderB = b.DashboardConf?.order ?? Infinity
        
        if (orderA === orderB) {
            // If both have no order or same order, sort by ID
            return (a.DashboardID || 0) - (b.DashboardID || 0)
        }
        
        return orderA - orderB
    })
})
export const ActiveDashboard = computed(() => {
    return activeDashboardState.value.activeDashboardID
})
export const ActiveDashboardData = computed(() => {
    return DashboardCachedDataService.dataRef.value.find(({ DashboardID }) => ActiveDashboard.value === DashboardID)
})
export const ActiveDashboardDataConfig = computed(() => {
    return  ActiveDashboardData.value?.DashboardConf
})
export const ActiveDashboardName = computed(() => {
    return ActiveDashboardData.value?.DashboardTitle
})
export const ActiveWidgetGroupID = computed(() => {
    return activeDashboardState.value.activeWidgetGroupID
})

/* Methods */
export function getActiveDashboardData () {
    return ActiveDashboardData.value
}

function updateActiveDashboardState (key: keyof ActiveStoreDashboardType, value: number | undefined) {
    activeDashboardState.value = {
        ...activeDashboardState.value,
        [key]: value
    }
}

export function setActiveDashboard (dashboard: IDashboard) {
    updateActiveDashboardState('activeDashboardID', dashboard.DashboardID)
    // if (!dashboard['DashboardLayoutID']) {
    //     dashboard['DashboardLayoutID'] = DEFAULT_LAYOUT_ID
    //     await DashboardApi.update(dashboard)
    //     await GlobalCachedDataService.loadServiceData('DashboardCachedDataService')
    // }
    const widgetGroupList = dashboard?.WidgetGroupList || []
    const activeGroupId = ActiveWidgetGroupID.value
    const existedGroup = activeGroupId ? widgetGroupList.find(i => i.WidgetGroupID === activeGroupId) : undefined
    const widgetGroupID = existedGroup?.WidgetGroupID ?? widgetGroupList[0]?.WidgetGroupID
    setActiveWidgetGroupID(widgetGroupID)
}

export function setFirstWidgetGroupAsActive () {
    const widgetGroupList = ActiveDashboardData.value?.WidgetGroupList || []
    const widgetGroupID = widgetGroupList[0]?.WidgetGroupID
    setActiveWidgetGroupID(widgetGroupID)
}

export function setActiveWidgetGroupID (groupId: number | undefined) {
    updateActiveDashboardState('activeWidgetGroupID', groupId)
}

/* ---ACTIONS--- */
export async function selectDashboard (dashboardId?: number) {
    logger.time('ALL selectDashboard METHOD')
    logger.time('selectDashboard 1')
    let dashboardIDToSet: undefined | number = undefined
    const currentDashboardId = activeDashboardState.value.activeDashboardID

    const availableDashboardIDs = await DashboardApi.getAccountDashboardList()

    // Filter cached dashboards based on their existence
    const filteredActiveAccountDashboards = ActiveAccountDashboards.value.filter(({ DashboardID }) => DashboardID && availableDashboardIDs.includes(DashboardID))

    logger.timeEnd('selectDashboard 1')

    logger.time('selectDashboard 2')

    if (dashboardId === undefined) {
        logger.log('WANT TO SET EITHER CURRENT EITHER FIRST')

        if (currentDashboardId) {
            // To get either currently selected dashboard data, or first one
            dashboardIDToSet = filteredActiveAccountDashboards.find(({ DashboardID }) => DashboardID === currentDashboardId)?.DashboardID
        }

        if (!dashboardIDToSet) {
            dashboardIDToSet = filteredActiveAccountDashboards[0]?.DashboardID
        }
    } else {
        logger.log('WANT TO SET SPECIFIC')

        // Check if dashboard we want to set exists
        dashboardIDToSet = filteredActiveAccountDashboards.find(dashboard => dashboard.DashboardID === dashboardId)?.DashboardID
    }

    logger.timeEnd('selectDashboard 2')
    logger.time('selectDashboard 3')

    if (dashboardIDToSet === undefined) {
        logger.log('HAVE NO DASHBOARDS TO SET, CHECKING IF THERE WILL BE DASHBOARD AFTER DATA LOAD')

        if (!GlobalCachedDataService.isAllAPICallsFulfilled.value) {
            await new Promise((res) => {
                GlobalCachedDataService.onAllDataRefreshed(res)
            })
        }

        if (ActiveAccountDashboards.value[0]?.DashboardID === undefined) {
            logger.log('AFTER DATA LOAD NO DASHBOARDS FOUND')
            return clearActiveDashboard()
        } else {
            logger.log('AFTER DATA LOAD SETTING FIRST DASHBOARD')

            return setActiveDashboard(ActiveAccountDashboards.value[0])
        }
    }

    logger.timeEnd('selectDashboard 3')
    logger.time('selectDashboard 4')

    // At this point dashboardIDToSet has an ID of dashboard that exists

    // Filter all dashboards that exists
    const filteredDashboardsThatExists = DashboardCachedDataService.dataRef.value.filter(({ DashboardID }) => DashboardID && availableDashboardIDs.includes(DashboardID))
    const dashboardToSetIndexInFilteredDashboards = filteredDashboardsThatExists.findIndex(({ DashboardID }) => DashboardID === dashboardIDToSet)
    const cachedDashboardToSet = filteredDashboardsThatExists[dashboardToSetIndexInFilteredDashboards]

    let newDashboardData: undefined | IDashboard = undefined

    if ('extended' in cachedDashboardToSet) {
        logger.log('SETTING EXTENDED DASHBOARD FROM CACHE')
        
        // Update lastAccessedAt timestamp for LRU
        const updatedDashboard: IExtendedDashboard = {
            ...cachedDashboardToSet,
            lastAccessedAt: Date.now()
        }
        
        // Update the cache with new timestamp
        const dashboardCachedDataToSet: Array<IDashboard | IExtendedDashboard> = [ ...filteredDashboardsThatExists ]
        dashboardCachedDataToSet[dashboardToSetIndexInFilteredDashboards] = updatedDashboard
        
        await GlobalCachedDataService.updateServiceData(
            'DashboardCachedDataService',
            dashboardCachedDataToSet
        )
        
        newDashboardData = updatedDashboard
    } else {
        logger.log('FETCHING DASHBOARD DATA FROM API')
        try {
            newDashboardData = await DashboardApi.find(dashboardIDToSet)

            if (newDashboardData) {
                logger.log('SETTING DASHBOARD DATA FROM API TO CACHE')

                // Simply copy the existing array and update only the newly fetched dashboard
                const dashboardCachedDataToSet: Array<IDashboard | IExtendedDashboard> = [ ...filteredDashboardsThatExists ]
                
                dashboardCachedDataToSet.splice(
                    dashboardToSetIndexInFilteredDashboards,
                    1,
                    {
                        ...newDashboardData,
                        extended: true,
                        lastAccessedAt: Date.now()  // Mark access time for LRU
                    }
                )

                // ✅ LRU Cache Management: Remove oldest extended dashboards if limit exceeded
                const extendedDashboards = dashboardCachedDataToSet.filter((d): d is IExtendedDashboard => 'extended' in d)
                if (extendedDashboards.length > MAX_EXTENDED_DASHBOARDS) {
                    logger.log(`Cache limit exceeded (${extendedDashboards.length}/${MAX_EXTENDED_DASHBOARDS}), removing oldest dashboards`)
                    
                    // Sort by lastAccessedAt (oldest first)
                    const sortedByAge = [ ...extendedDashboards ].sort((a, b) => 
                        (a.lastAccessedAt || 0) - (b.lastAccessedAt || 0)
                    )
                    
                    // Get IDs of dashboards to remove (oldest ones beyond the limit)
                    const dashboardsToRemoveCount = extendedDashboards.length - MAX_EXTENDED_DASHBOARDS
                    const dashboardIDsToRemove = new Set(
                        sortedByAge.slice(0, dashboardsToRemoveCount).map(d => d.DashboardID)
                    )
                    
                    // Convert extended dashboards back to basic dashboards
                    for (let i = 0; i < dashboardCachedDataToSet.length; i++) {
                        const dashboard = dashboardCachedDataToSet[i]
                        if ('extended' in dashboard && dashboardIDsToRemove.has(dashboard.DashboardID)) {
                            // Remove extended flag and widget data
                            const { extended, lastAccessedAt, ...basicDashboard } = dashboard
                            dashboardCachedDataToSet[i] = {
                                ...basicDashboard,
                                WidgetGroupList: []
                            }
                            logger.log(`Removed extended cache for Dashboard ${dashboard.DashboardID}`)
                        }
                    }
                }

                await GlobalCachedDataService.updateServiceData(
                    'DashboardCachedDataService',
                    dashboardCachedDataToSet
                )
            }
        } catch (e) {
            console.error(e)
        }
    }

    logger.timeEnd('selectDashboard 4')
    logger.time('selectDashboard 5')

    if (newDashboardData) {
        logger.log('SETTING DASHBOARD')
        setActiveDashboard(newDashboardData)
    } else {
        logger.log('NO DASHBOARD TO SET')
        clearActiveDashboard()
    }

    logger.timeEnd('selectDashboard 5')

    logger.timeEnd('ALL selectDashboard METHOD')
}

export function clearActiveDashboard () {
    activeDashboardState.value = {
        activeDashboardID: undefined,
        activeWidgetGroupID: undefined
    }
    editModeState.value = false
}

export async function updateDashboard (dashboard: IDashboard) {
    await DashboardApi.update(dashboard)
    await resetDashboardCachedDataService()
    if (dashboard.DashboardID === ActiveDashboard.value) {
        await selectDashboard(dashboard.DashboardID)
    }
}

export async function createDashboard (dashboardData: IDashboardCreate, select = true) {
    const dashboard = await DashboardApi.store(dashboardData)

    if (dashboard && select) {
        clearActiveDashboard()
        await resetDashboardCachedDataService()
        await selectDashboard(dashboard.DashboardID)
    }
    return dashboard
}

export async function deleteDashboard (dashboard: IDashboard) {
    if (dashboard.DashboardID) {
        await DashboardApi.destroy(dashboard.DashboardID)
        await resetDashboardCachedDataService()
        if (dashboard.DashboardID === ActiveDashboard.value) {
            clearActiveDashboard()
            await selectDashboard()
        }
    }
}

export async function removeActiveDashboardWidgetGroup (widgetGroupId: number) {
    if (!ActiveDashboardData.value?.DashboardID) {
        return
    }

    const widgetGroup = ActiveDashboardData.value.WidgetGroupList?.find(wg => wg.WidgetGroupID === widgetGroupId)

    if (widgetGroup?.WidgetList?.length) {
        await Promise.all(
            widgetGroup.WidgetList.map(el => WidgetApi.destroy(el.WidgetID))
        )
    }

    await DashboardApi.removeWidgetGroup(
        ActiveDashboardData.value.DashboardID,
        widgetGroupId
    )

    setFirstWidgetGroupAsActive()
}


export async function resetDashboardCachedDataService () {
    await GlobalCachedDataService.loadServiceData('DashboardCachedDataService')
}

export async function updateDashboardLayout (layoutID: number) {
    if (!ActiveDashboardData.value) {
        return
    }
    await updateDashboard({
        ...ActiveDashboardData.value,
        DashboardLayoutID: layoutID
    })
}
